import React, { useEffect, useRef, useState } from 'react';
import { Image, Button, Form } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { S3_URL } from 'constants';
import { useAddCommentMutation, useCheckHashTagsMutation } from 'services';
import { extractPostData, extractTagsAndMentions } from 'utils';
import { setIsLoading } from 'store/common';
import MentionsComponent from 'components/Common/MentionsComponent/input';
import { useFormik } from 'formik';
import { addCommentInitialValues, addCommentSchema } from 'constants/schemaValidations';
import useTranslate from 'hooks/useTranslate';
import useAuthStore from 'store/auth';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import './styles.scss';
import useLocalizationStore from 'store/localization';

interface FeedCommentsProps {
	feedDetails: any;
	setTotalNumberOfComments?: any;
	setNumberOfReplies?: any;
	onAddNewComment?: any;
}

const CommentForm: React.FC<FeedCommentsProps> = ({
	feedDetails,
	setTotalNumberOfComments,
	setNumberOfReplies = () => {},
	onAddNewComment = () => {},
}) => {
	const { translate } = useTranslate();
	const { activeLanguage }: any = useLocalizationStore((state) => state);
	const { user: authUser }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { mutateAsync: addCommentMutation } = useAddCommentMutation();
	const { mutateAsync: checkHashTagsMutation } = useCheckHashTagsMutation();
	const { postId, commentId = null } = feedDetails;
	const emojiRef = useRef<any>(null);
	const emojiBtnRef = useRef<any>(null);

	const [openEmojiPicker, setOpenEmojiPicker] = useState<boolean>(false);

	const formik: any = useFormik({
		initialValues: { ...addCommentInitialValues, postId, parentCommentId: commentId },
		validationSchema: addCommentSchema(translate),
		enableReinitialize: true,
		onSubmit: async (values: any, { setFieldValue, setFieldError }) => {
			if (!values?.comment.trim()) {
				setFieldValue('content', values?.comment.trim());
				setFieldError(
					'content',
					translate('ToastMessage.titleCannotBeEmpty', { title: translate('Modal.comment') }),
				);
				return;
			}

			setIsLoading(true);
			const { taggedUsers, hashTags } = await extractTagsAndMentions(values.comment.trim());

			const hashTagList: any = hashTags?.length > 0 ? hashTags?.map((res) => res.name) : [];
			let oldHashTag: any = []; // Array of IDs only
			let newHashTagsArr: any = [];

			if (hashTagList?.length > 0) {
				await checkHashTagsMutation({ hashTag: hashTagList }).then((res) => {
					if (res.success) {
						oldHashTag = res?.data?.map((hash: any) => hash.id); // Array of IDs only
						const oldHashTagNames = res?.data?.map((hash: any) => hash.hashTag); // Array of hashTag names for case-sensitive check

						const remainingHashTag = hashTags.filter(
							(item) =>
								!oldHashTag.includes(item.id) && // Filter by ID
								!oldHashTagNames.includes(item.name), // Case-sensitive filter by name
						);

						newHashTagsArr = remainingHashTag.map((hash) => hash.name); // Array of new hashtag names
					}
				});
			}

			const modifiedData = {
				...values,
				comment: values?.comment?.trim(),
				hashTags: oldHashTag,
				newHashTags: newHashTagsArr,
				taggedUsers,
			};
			setFieldValue('content', values?.comment.trim());
			addCommentMutation(modifiedData)
				.then((res: any) => {
					if (res?.success) {
						if (res?.data) {
							onAddNewComment(res.data);
						}
						// setNumberOfReplies((rev: number) => Number(rev) + 1);
						setTotalNumberOfComments((rev: number) => Number(rev) + 1);
						formik.resetForm();
					}
				})
				.finally(() => setIsLoading(false));
		},
	});

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [openEmojiPicker]);

	const handleAddEmoji = (emojiData: any) => {
		const emoji = emojiData.native;
		const messageInput: any = document.getElementById(
			`textarea_${commentId}`,
		) as HTMLTextAreaElement;
		if (messageInput) {
			const { selectionStart, selectionEnd } = messageInput;
			const comment = formik.values.comment || '';
			const newMessage = comment.slice(0, selectionStart) + emoji + comment.slice(selectionEnd);
			formik.setFieldValue('comment', newMessage);
			setTimeout(() => {
				messageInput.selectionStart = messageInput.selectionEnd = selectionStart + emoji.length;
			}, 0);
		}
	};

	const handleClickOutside = (e: any) => {
		if (!emojiRef?.current?.contains(e.target) && !emojiBtnRef?.current?.contains(e.target)) {
			setOpenEmojiPicker(e.unified);
		}
	};

	return (
		<Form onSubmit={formik.handleSubmit}>
			<div className="social-post-comment-box rounded-pill d-flex align-items-center gap-2 gap-sm-3">
				<div className="comment-user-img rounded-circle">
					<Image
						src={
							authUser?.profilePicture ? `${S3_URL}${authUser?.profilePicture}` : IMAGE_PATHS.UserBG
						}
						className="w-100 h-100 object-fit-cover rounded-circle"
						loading="lazy"
					/>
				</div>

				<div className="comment-input flex-1">
					<MentionsComponent
						formik={formik}
						fieldName={'comment'}
						textAreaId={`textarea_${commentId}`}
					/>
				</div>
				<div className="comment-actions d-flex gap-2 gap-sm-3 position-relative">
					<Button className="border-0 p-0 bg-transparent" type="submit">
						<i className="ri-send-plane-fill"></i>
					</Button>
					<Button
						className="border-0 p-0 bg-transparent"
						onClick={() => setOpenEmojiPicker(!openEmojiPicker)}
						ref={emojiBtnRef}
					>
						<i className="ri-emotion-happy-fill"></i>
					</Button>
					{openEmojiPicker && (
						<div className="emoji-picker-box" ref={emojiRef}>
							<Picker
								data={data}
								onEmojiSelect={handleAddEmoji}
								showPreview={false}
								className="emoji-picker"
								locale={activeLanguage}
								emojiButtonSize={30}
								emojiSize={18}
								previewPosition={'none'}
							/>
						</div>
					)}
				</div>
			</div>
		</Form>
	);
};

export default React.memo(CommentForm);
